import { BaseDataStoreReadOnly } from "./BaseDataStoreReadOnly";
import { BaseEntity } from "../entities/BaseEntities";
import { BaseDataStoreCRUD } from "./BaseDataStoreCRUD";
import { BaseEndpointCRUD } from "../endpoint/BaseEndpointCRUD";
import { BaseDataSourceAbstract } from "./BaseDataSourceAbstract";
import { BaseDataStoreAbstract } from "./BaseDataStoreAbstract";
import { Observable } from "rxjs";
import { AppInjector } from "@core/injector/injector.module";
import { DataLoadOptions } from "./DataLoadOptions";
import { Utils } from "@core/helpers/Utils";
import { GenericListEndpoint } from "@core/endpoints/genericlist.endpoint";
import { BaseEndpoint } from '../endpoint/BaseEndpoint';
import { Type } from '@angular/core';
import { BaseDataStoreCRUDServer } from "./BaseDataStoreCRUDServer";



/*
 The idea is as follows:
 There are 2 objects - datasource and datastore
 Datastore defines the data
 Datasource defines the query to the data
 In reality, they are linked close together
 There are 6 datastore types to help construction them fast
 Each also creates a new datastore inside

 Basically the choices are between :
 1. Calling a method on the WebAPI directly (Direct, DirectService)
 2. Calling a method on a service in code (CustomAction)
 3. Accessing a generic list data provider (GenericListI, GenericListS)
 4. Accessing the data through a CRUD service's Load() for it's respective entity

The datasources can be defined in two places:
1. In the component constructor - in the DataSources array 
2. In Init.ts, defined as a global datasource that is accessible from all components

The data is loaded into the datasource once per it's lifetime
There are 3 types of datasources - "stupid/raw" and "smart" ones:
1. The stupid datasources only need to provide the data once using a method that returns BaseEntitiesResponse. 
   Further processing is done by the datasource itself. 
   This means that the server always must return the full dataset

2. The smart datasources need to provide data multiple times depending on the user-entered value
    The data filtering and paging will be done on the server and a small subset will be returned
    To implement this, two methods must be provided 
    1. A method that takes DataLoadOptions and returns BaseEntitiesResponse
    2. A method that returns an object by it's id


*** A word about GenericListProvider ***
 * The GenericList functions are implemented to work in both methods
 * 
 
    
 */




//Completely custom
export class DataSourceCustom extends BaseDataSourceAbstract {
    constructor(store: BaseDataStoreAbstract) {
        super(store);
    }
}

//Create datasource based on CRUD service
export class DataSourceEndpointCRUD<TEntity extends BaseEntity> extends BaseDataSourceAbstract {
    constructor(service: BaseEndpointCRUD<TEntity>, customOptions?: any) {
        super(new BaseDataStoreCRUD<TEntity>(service), customOptions);
    }
}

//Create serverside datasource based on CRUD service
export class ServerSideDataSourceEndpointCRUD<TEntity extends BaseEntity> extends BaseDataSourceAbstract {
    constructor(service: BaseEndpointCRUD<TEntity>, customOptions?: any, customLoadAction?: (options: DataLoadOptions) => Observable<any>) {
        super(new BaseDataStoreCRUDServer<TEntity>(service, false, customLoadAction), customOptions);
    }
}


//Datasource that is directly bound to a controller action on the server
export class DataSourceDirect extends BaseDataSourceAbstract {
    constructor(ControllerName: string, ActionName: string, QueryString?: string, customOptions?: any) {
        super(new BaseDataStoreReadOnly(ControllerName, ActionName, QueryString), customOptions);
    }
}
//Datasource that is directly bound to a controller action on the server, taking the controller name from an existing service that represents it
export class DataSourceDirectService extends BaseDataSourceAbstract {
    constructor(serviceType: Type<BaseEndpoint>, ActionName: string, QueryString?: string, customOptions?: any, isRaw: boolean = true) {
        const controllerName = AppInjector.get(serviceType).ControllerName;
        super(new BaseDataStoreReadOnly(controllerName, ActionName, QueryString, undefined, undefined, undefined, isRaw), customOptions);
    }
}


//Datasource that is bound to a function returning the data
export class DataSourceCustomAction extends BaseDataSourceAbstract {
    constructor(
        customLoadAction: (options: DataLoadOptions) => Observable<any>,
        customGetAction?: (Value: any) => Observable<any>,
        OnLoadedHandler?: (Data: any[]) => void,
        IsRaw: boolean = true,
        customOptions?: any,
        keyExpr?: any) {
        super(
            new BaseDataStoreReadOnly
                (
                null,
                null,
                null,
                customLoadAction,
                customGetAction,
                OnLoadedHandler,
                IsRaw,
                keyExpr),
            customOptions);
    }
}

//Datasource bound to a generic list (string)
export class DataSourceGenericListS extends BaseDataSourceAbstract {
    constructor(DataSourceName: string, IsRaw: boolean = true) {
        super(
            new BaseDataStoreReadOnly
                (
                null,
                null,
                null,
                (options) => AppInjector.get(GenericListEndpoint).GetList(DataSourceName, options),
                IsRaw ? undefined : (Value) => AppInjector.get(GenericListEndpoint).GetByValue(DataSourceName, Value),
                undefined,
                IsRaw,
                "Value"
                ),
            {
                sort: "Text"
            });
    }
}

//Datasource bound to a generic list (numeric)
export class DataSourceGenericListI extends BaseDataSourceAbstract {
    constructor(DataSourceName: string, IsRaw: boolean = true) {
        super(
            new BaseDataStoreReadOnly
                (
                null,
                null,
                null,
                //Function that gets the data
                (options) => AppInjector.get(GenericListEndpoint).GetList(DataSourceName, options),
                IsRaw ? undefined : (Value) => AppInjector.get(GenericListEndpoint).GetByValue(DataSourceName, Value),
                undefined,
                IsRaw,
                "Value"
                ),
            {
                //Function that maps the data
                map: (item) => Utils.MapAsNumeric(item),
                sort: "Text"
            });
    }


}





